import React, { useState, useEffect } from 'react';
import { apiService, graphBuilderApi } from '../../services/api';
import './CopyToGraphModal.css';

/**
 * Modal component for copying a process to another graph.
 * Allows the user to select a target graph and optionally rename the process.
 */
function CopyToGraphModal({ process, currentGraphId, onClose, onSuccess }) {
  // State
  const [graphs, setGraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGraphId, setSelectedGraphId] = useState('');
  const [newName, setNewName] = useState(`${process?.name || ''} (Copy)`);

  // Fetch available graphs
  useEffect(() => {
    const fetchGraphs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all graphs - may need pagination for large lists
        const response = await apiService.listGraphs(1, 100);
        const graphList = response.graphs || response.data || response || [];
        
        const filteredGraphs = currentGraphId
          ? graphList.filter(g => 
              g.graphId !== currentGraphId && g.graph_id !== currentGraphId
            )
          : graphList;
        
        setGraphs(filteredGraphs);
        
        // Auto-select first graph if available
        if (filteredGraphs.length > 0) {
          setSelectedGraphId(filteredGraphs[0].graphId || filteredGraphs[0].graph_id);
        }
      } catch (err) {
        console.error('Error fetching graphs:', err);
        setError('Failed to load graphs');
      } finally {
        setLoading(false);
      }
    };

    fetchGraphs();
  }, [currentGraphId]);

  // Handle copy
  const handleCopy = async () => {
    if (!selectedGraphId) {
      setError('Please select a target graph');
      return;
    }

    try {
      setCopying(true);
      setError(null);

      await graphBuilderApi.copyProcessToGraph(
        process.process_id,
        selectedGraphId,
        newName || null
      );

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error copying process:', err);
      setError(err.message || 'Failed to copy process');
    } finally {
      setCopying(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="copy-to-graph-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Copy Process to Another Graph</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Source Process Info */}
          <div className="form-group">
            <label>Source Process</label>
            <div className="source-info">
              <span className="process-name">{process?.name}</span>
              {process?.description && (
                <span className="process-description">{process.description}</span>
              )}
            </div>
          </div>

          {/* Target Graph Selector */}
          <div className="form-group">
            <label htmlFor="target-graph">Target Graph *</label>
            {loading ? (
              <div className="loading-inline">
                <span className="spinner-small"></span>
                <span>Loading graphs...</span>
              </div>
            ) : graphs.length === 0 ? (
              <div className="no-graphs-message">
                No other graphs available. Create a new graph first.
              </div>
            ) : (
              <select
                id="target-graph"
                value={selectedGraphId}
                onChange={(e) => setSelectedGraphId(e.target.value)}
                disabled={copying}
              >
                <option value="">Select a graph...</option>
                {graphs.map((graph) => (
                  <option 
                    key={graph.graphId || graph.graph_id} 
                    value={graph.graphId || graph.graph_id}
                  >
                    {graph.graphName || graph.name || graph.graph_name || 'Unnamed Graph'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* New Name Input */}
          <div className="form-group">
            <label htmlFor="new-name">New Process Name</label>
            <input
              id="new-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new name (optional)"
              disabled={copying}
            />
            <span className="help-text">Leave empty to keep the original name with "(Copy)" suffix</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Info Box */}
          <div className="info-box">
            <span className="info-icon">ℹ️</span>
            <span>
              Copying will create a new process with all Platform node references 
              updated to point to the selected graph.
            </span>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={copying}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleCopy}
            disabled={copying || !selectedGraphId || graphs.length === 0}
          >
            {copying ? (
              <>
                <span className="spinner-small"></span>
                Copying...
              </>
            ) : (
              'Copy Process'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CopyToGraphModal;
